import { useState } from 'react'
import { Copy, RotateCw } from 'lucide-react'
import { toast } from 'sonner'
import { AppShell } from '@/components/layout/app-shell'
import { partnerApi } from '@/lib/api'

export function ApiKeysPage() {
  const [rotating, setRotating] = useState(false)
  const [newCreds, setNewCreds] = useState<{ api_key: string; hmac_secret: string } | null>(null)

  const handleRotate = async () => {
    if (!confirm('¿CONFIRMAR rotación? Las credenciales actuales quedarán INVALIDADAS inmediatamente. Cualquier integración activa fallará hasta actualizar las nuevas credenciales.')) return
    setRotating(true)
    try {
      const r = await partnerApi.regenerateApiKeys()
      setNewCreds(r)
      toast.success('Credenciales rotadas')
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } }
      toast.error(`No se pudo rotar: ${e?.response?.data?.message ?? 'error desconocido'}`)
    } finally {
      setRotating(false)
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl">
        <h1 className="text-3xl font-bold">API Keys</h1>
        <p className="mt-2 text-sm text-fg-secondary">
          Credenciales server-to-server (HMAC) para tu integración ERP.
        </p>

        <div className="mt-6 rounded-md border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-900 dark:bg-yellow-950 dark:text-yellow-100 dark:border-yellow-800">
          <strong>⚠ Operación crítica.</strong> Rotar invalida las credenciales actuales <em>inmediatamente</em>. Hazlo solo cuando: sospechas de leak, rotación periódica programada, o cambio de integrador.
        </div>

        <div className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
          <button
            type="button"
            onClick={handleRotate}
            disabled={rotating}
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RotateCw className={`h-4 w-4 ${rotating ? 'animate-spin' : ''}`} />
            {rotating ? 'Rotando...' : 'Rotar credenciales'}
          </button>
        </div>

        {newCreds && (
          <CredentialsModal creds={newCreds} onDismiss={() => setNewCreds(null)} />
        )}
      </div>
    </AppShell>
  )
}

/**
 * Modal de una sola exhibición de las credenciales nuevas.
 *
 * - No se puede cerrar con click afuera ni Escape: SOLO con el botón
 *   "Ya las guardé" (tras marcar el checkbox de confirmación).
 * - Al cerrar, las credenciales salen del state del componente padre y
 *   no se vuelven a mostrar. Si el usuario navega a otra página, el
 *   state (useState) muere con el unmount — no queda copia en memoria
 *   de la app ni en storage.
 */
function CredentialsModal({
  creds,
  onDismiss,
}: {
  creds: { api_key: string; hmac_secret: string }
  onDismiss: () => void
}) {
  const [acked, setAcked] = useState(false)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Nuevas credenciales"
        className="w-full max-w-lg rounded-xl border border-yellow-300 bg-bg-surface p-6 shadow-xl dark:border-yellow-800"
      >
        <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-100">
          ⚠ Nuevas credenciales — se muestran UNA SOLA VEZ
        </p>
        <p className="mt-1 text-xs text-fg-secondary">
          Cópialas a tu gestor de secretos ahora. Al cerrar este diálogo no se
          volverán a mostrar; si las pierdes tendrás que rotar de nuevo.
        </p>

        <div className="mt-4 space-y-3">
          <CredRow label="API Key" value={creds.api_key} />
          <CredRow label="HMAC Secret" value={creds.hmac_secret} />
        </div>

        <label className="mt-5 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={acked}
            onChange={(e) => setAcked(e.target.checked)}
            className="mt-0.5"
          />
          <span>Confirmo que copié ambas credenciales en un lugar seguro.</span>
        </label>

        <button
          type="button"
          onClick={onDismiss}
          disabled={!acked}
          className="mt-4 w-full rounded-md bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Ya las guardé
        </button>
      </div>
    </div>
  )
}

function CredRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium text-fg-muted">{label}</p>
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 break-all rounded-md bg-bg-elevated px-3 py-2 font-mono text-xs">{value}</code>
        <button
          type="button"
          onClick={() => { navigator.clipboard.writeText(value); toast.success(`${label} copiado`) }}
          className="rounded-md border border-border p-2 hover:bg-bg-elevated"
          title={`Copiar ${label}`}
        >
          <Copy className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
